import { each } from 'underscore';
import t from 'react-translate';
import { CourseAliases } from 'redux/schemas/models/course';
import { LecturePageType } from 'redux/schemas/models/lecture-page';
import { OwnerType } from 'redux/schemas/models/course-communication';
import { ActivityType } from 'redux/schemas/models/activity';

import {
  RteTag, RteTagTypes, RelatedActivity, RteTagValue,
} from './nv-froala-constants';

// Export Default Tags that are Course Depended
/* @ngInject */
export function getDefaultTags(
  isSelfPaced: boolean,
  aliases: CourseAliases,
): { [key: string]: RteTag } {
  return {
    FIRST_NAME: {
      tagValue: RteTagValue.FIRST_NAME,
      displayName: t.FROALA.INSERT_TAGS.LEARNER_FIRST_NAME(aliases.learnersAliases),
      type: RteTagTypes.NAME,
    },
    LAST_NAME: {
      tagValue: RteTagValue.LAST_NAME,
      displayName: t.FROALA.INSERT_TAGS.LEARNER_LAST_NAME(aliases.learnersAliases),
      type: RteTagTypes.NAME,
    },
    OFFERING_NAME: {
      tagValue: RteTagValue.OFFERING_NAME,
      displayName: t.FROALA.INSERT_TAGS.COURSE_NAME(aliases.courseAliases),
      type: RteTagTypes.NAME,
    },
    OFFERING_HOME_URL: {
      tagValue: RteTagValue.OFFERING_HOME_URL,
      displayName: t.FROALA.INSERT_TAGS.COURSE_HOME(aliases.courseAliases),
      type: RteTagTypes.URL,
    },
    OFFERING_START_DATE: {
      tagValue: RteTagValue.OFFERING_START_DATE,
      displayName: t.FROALA.INSERT_TAGS.COURSE_OPENING_DATE(aliases.courseAliases),
      type: RteTagTypes.DATE,
    },
    OFFERING_RELEASE_DATE: {
      tagValue: RteTagValue.OFFERING_RELEASE_DATE,
      displayName: t.FROALA.INSERT_TAGS.COURSE_RELEASE_DATE(aliases.courseAliases),
      type: RteTagTypes.DATE,
    },
    OFFERING_END_DATE: {
      tagValue: RteTagValue.OFFERING_END_DATE,
      displayName: t.FROALA.INSERT_TAGS.COURSE_END_DATE(aliases.courseAliases),
      type: RteTagTypes.DATE,
    },
    OFFERING_CLOSING_DATE: {
      tagValue: RteTagValue.OFFERING_CLOSING_DATE,
      displayName: t.FROALA.INSERT_TAGS.COURSE_CLOSING_DATE(aliases.courseAliases),
      type: RteTagTypes.DATE,
    },
    ...(isSelfPaced ? {
      OFFERING_REMAINING_ACCESS_DAYS: {
        tagValue: RteTagValue.OFFERING_REMAINING_ACCESS_DAYS,
        displayName: t.FROALA.INSERT_TAGS.REMAINING_DAYS_FOR_ACCESS(),
        type: RteTagTypes.DATE,
      },
    } : {}),
  };
}

export const getExtraTags = (tagValues: RteTagValue[]): { [ key: string]: RteTag } => {
  // Return only the reuested tags from parent component
  const extraRteTags = {
    [RteTagValue.INSERT_PASSWORD_SENTENCE]: {
      tagValue: RteTagValue.INSERT_PASSWORD_SENTENCE,
      displayName: t.FROALA.INSERT_TAGS.INSERT_PASSWORD_SENTENCE(),
      type: RteTagTypes.ACCESS_INSTRUCTION,
    },
  };
  const requestedTags: { [key: string]: RteTag } = {};
  each(tagValues, tagvalue => {
    requestedTags[tagvalue] = extraRteTags[tagvalue];
  });
  return requestedTags;
};

// Convert OwnerType ( Activity + LecturePage ) to the tag Format
export const getRteTagPrefixFromActivityType = (type: OwnerType, isFeedbackPublic: boolean) => {
  switch (type) {
    case ActivityType.EXERCISE:
      return 'ASSIGNMENT';
    case ActivityType.QUIZ:
      return 'QUIZ';
    case ActivityType.TIMED_EXAM:
      return 'TIMED_EXAM';
    case ActivityType.SURVEY:
      return 'SURVEY';
    case ActivityType.TEAM_FORMATION:
      return 'TEAM_FORMATION';
    case ActivityType.GROUP_FORMATION:
      return 'GROUP_FORMATION';
    case ActivityType.POST:
      return 'DISCUSSION';
    case ActivityType.TEAM_DISCUSSION:
      return 'TEAM_DISCUSSION';
    case ActivityType.PEER_EVALUATION:
      return isFeedbackPublic ? 'PUBLIC_FEEDBACK' : 'PRIVATE_FEEDBACK';
    case ActivityType.EXTERNAL_TOOL:
      return 'THIRD_PARTY_TOOL';
    case ActivityType.LIVE_SESSION:
      return 'LIVE_VIDEO_EVENT';
    case ActivityType.LECTURE_VIDEO:
      return 'AUDIO_VIDEO';
    case ActivityType.PROFILE_REQUIREMENT:
      return 'PROFILE_COMPLETION';
    case ActivityType.POLL:
      return 'POLL';
    case LecturePageType.LECTURE:
      return 'LESSON';
    case ActivityType.VIDEO_PRACTICE:
      return 'VIDEO_PRACTICE';
    case ActivityType.VIDEO_PRACTICE_FEEDBACK:
      return 'FEEDBACK_CRITERIA';
    default:
      return '';
  }
};

// Place the tags to the RTE editor
/* @ngInject */
export function insertRteTags(
  {
    tagValue,
    displayName,
  }: RteTag,
  froalaInstanceRef,
): void {
  froalaInstanceRef.current.selection.restore();
  froalaInstanceRef.current.html.insert(`[${tagValue}]`);
  froalaInstanceRef.current.undo.saveStep(); // save current HTML into the undo stack
}

// Create Actual Tag for a Lecture Entity ( Activity / Lesson )
/* @ngInject */
export function createRteTagValue(
  entityType: OwnerType,
  entityId: number,
  tagType: RteTagTypes,
  isFeedbackPublic: boolean,
): string {
  if (tagType === RteTagTypes.URL) {
    // URL tag
    return `${getRteTagPrefixFromActivityType(entityType, isFeedbackPublic)}_${entityId}_URL`;
  }
  if (entityType === LecturePageType.LECTURE) {
    // Lecture Release Tag
    return `${getRteTagPrefixFromActivityType(entityType, isFeedbackPublic)}_${entityId}_RELEASE_DATE`;
  }
  if (entityType === ActivityType.LIVE_SESSION) {
    // Live Stream Start Tag
    return `${getRteTagPrefixFromActivityType(entityType, isFeedbackPublic)}_${entityId}_START_DATE`;
  }
  // Assignment Due Date Tag
  return `${getRteTagPrefixFromActivityType(entityType, isFeedbackPublic)}_${entityId}_DUE_DATE`;
}

// Created Tag Object From Passed Activity / Lesson object
/* @ngInject */
export function convertRelatedActivityToRteTag(
  activity: RelatedActivity,
  tagType: RteTagTypes,
  defaultSelected?: boolean,
  isFeedbackPublic?: boolean,
): RteTag {
  return {
    tagValue: createRteTagValue(activity.type, activity.id, tagType, isFeedbackPublic),
    entityType: activity.type,
    entityId: activity.id,
    type: tagType,
    selected: !!defaultSelected,
  };
}
